import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Batches from "./pages/Batches";
import BatchForm from "./pages/BatchForm";
import BatchView from "./pages/BatchView";
import Products from "./pages/Products";
import Clients from "./pages/Clients";
import Suppliers from "./pages/Suppliers";
import Logistics from "./pages/Logistics";
import TransportCompanies from "./pages/TransportCompanies";
import Documents from "./pages/Documents";
import DocumentManagement from "./pages/DocumentManagement";
import Users from "./pages/Users";
import InvoiceOrigin from "./pages/InvoiceOrigin";
import InvoiceClient from "./pages/InvoiceClient";
import InvoiceOldView from "./pages/InvoiceOldView";
import PackingList from "./pages/PackingList";
import Labels from "./pages/Labels";
import Configuration from "./pages/Configuration";
import Reports from "./pages/Reports";
import SeguimientoCargas from "./pages/SeguimientoCargas";
import ActivityLogs from "./pages/ActivityLogs";
import InformeCso from "./pages/InformeCso";
import InformeLau from "./pages/InformeLau";
import InformeResumen from "./pages/InformeResumen";
import InformePrevisionLlegada from "./pages/InformePrevisionLlegada";
import Traceability from "./pages/Traceability";
import DiscountsReport from "./pages/DiscountsReport";
import Layout from "./components/Layout";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading, hasRole } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!hasRole("admin")) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Ruta que excluye al rol cliente
const NoClienteRoute = ({ children }) => {
  const { user, loading, hasRole } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (hasRole("cliente")) {
    return <Navigate to="/reports" replace />;
  }
  
  return children;
};

// Ruta solo para admin y admin_consultor (excluye cliente y proveedor)
const AdminConsultorRoute = ({ children }) => {
  const { user, loading, hasRole } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (hasRole("cliente") || hasRole("proveedor")) {
    return <Navigate to="/reports" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="batches" element={<Batches />} />
        <Route path="batches/new" element={<BatchForm />} />
        <Route path="batches/:id" element={<BatchForm />} />
        <Route path="batches/:id/view" element={<BatchView />} />
        <Route path="batches/:id/documents" element={<Documents />} />
        <Route path="batches/:id/invoice-origin" element={<InvoiceOrigin />} />
        <Route path="batches/:id/invoice-client" element={<InvoiceClient />} />
        <Route path="invoice-old-view/:invoiceId" element={<InvoiceOldView />} />
        <Route path="batches/:id/packing-list" element={<PackingList />} />
        <Route path="batches/:id/labels" element={<Labels />} />
        <Route path="batches/:id/informe-cso" element={<InformeCso />} />
        <Route path="documents" element={<DocumentManagement />} />
        <Route path="products" element={<Products />} />
        <Route path="clients" element={<Clients />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="logistics" element={<Logistics />} />
        <Route path="transport" element={<TransportCompanies />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reports/seguimiento-cargas" element={<SeguimientoCargas />} />
        <Route path="reports/informe-lau" element={<AdminConsultorRoute><InformeLau /></AdminConsultorRoute>} />
        <Route path="reports/informe-resumen" element={<NoClienteRoute><InformeResumen /></NoClienteRoute>} />
        <Route path="reports/prevision-llegada" element={<InformePrevisionLlegada />} />
        <Route path="reports/traceability" element={<Traceability />} />
        <Route path="reports/discounts" element={<DiscountsReport />} />
        <Route path="users" element={<AdminRoute><Users /></AdminRoute>} />
        <Route path="configuration" element={<AdminRoute><Configuration /></AdminRoute>} />
        <Route path="activity-logs" element={<AdminRoute><ActivityLogs /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
